import {
  ArrowLeft,
  Check,
  Clock,
  Edit2,
  Loader,
  MoreVertical,
  Trash2,
  UserCheck,
  UserX,
  X,
} from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { EvidenceCard } from '@/components/citations';
import { Button } from '@/components/ui/button';
import { Button as CatalystButton } from '@/components/ui/catalyst/button';
import {
  Dialog,
  DialogActions,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/catalyst/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Separator } from '@/components/ui/separator';
import { useDestinations } from '@/hooks/useDestinations';
import { DestinationScope } from '@/types/provider-destinations';

import {
  useApproveMedicalProviderMutation,
  useDeleteMedicalProviderMutation,
  useGetMedicalProviderQuery,
  useMarkProviderDoneTreatingMutation,
  useMarkProviderStillTreatingMutation,
  useRejectMedicalProviderMutation,
  useUpdateMedicalProviderMutation,
} from '../../../services/api/treatmentService';
import {
  ProviderStatus,
  ProviderType,
} from '../../../services/types/treatment-types';
import { OpenInDirectoryButton } from '../directory';
import { EncountersOverview } from '../encounters/EncountersOverview';
import { ProviderStatusBadge } from '../medical-provider/ProviderStatusBadge';
import { getSimpleTreatmentStatus, SimpleTreatmentStatus } from '../utils';
import { RecentRequestsList } from '../v3/provider/RecentRequestsList';
import { CreateProviderRequestDialog } from '../v3/request/modals/CreateRecordRequestModal';
import { ProviderInfoDisplay } from './ProviderInfoDisplay';

interface MedicalProviderDetailsProps {
  matterId: string;
  providerId: string;
  onBack: () => void;
  backButtonText?: string;
}

const DESTINATION_SCOPES: DestinationScope[] = [
  DestinationScope.RECORDS,
  DestinationScope.BILLS,
  DestinationScope.RADIOLOGY,
  DestinationScope.ER_PHYSICIAN,
];

const DESTINATION_SCOPE_LABELS: Record<DestinationScope, string> = {
  [DestinationScope.RECORDS]: 'Records',
  [DestinationScope.BILLS]: 'Bills',
  [DestinationScope.RADIOLOGY]: 'Radiology',
  [DestinationScope.ER_PHYSICIAN]: 'ER Physician',
};

export const MedicalProviderDetails = ({
  matterId,
  providerId,
  onBack,
  backButtonText = 'Back to List',
}: MedicalProviderDetailsProps) => {
  const navigate = useNavigate();
  const {
    data: provider,
    isLoading,
    error: loadError,
    refetch,
  } = useGetMedicalProviderQuery({
    matterId,
    providerId,
  });

  const [deleteProvider, { isLoading: isDeleting }] =
    useDeleteMedicalProviderMutation();
  const [updateProvider] = useUpdateMedicalProviderMutation();
  const [approveProvider] = useApproveMedicalProviderMutation();
  const [rejectProvider] = useRejectMedicalProviderMutation();
  const [markDoneTreating] = useMarkProviderDoneTreatingMutation();
  const [markStillTreating] = useMarkProviderStillTreatingMutation();

  const [isUpdating, setIsUpdating] = useState(false);
  const [isCreateRequestOpen, setIsCreateRequestOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const providerServiceId = provider?.provider_service_ref_id ?? '';
  const { getScopeDestinations, isLoading: isDestinationsLoading } =
    useDestinations(providerServiceId);

  const handleDelete = async () => {
    try {
      await deleteProvider({ matterId, providerId }).unwrap();
      toast.success('Provider deleted successfully');
      setIsDeleteDialogOpen(false);
      onBack();
    } catch (error) {
      toast.error('Failed to delete provider');
    }
  };

  const handleEdit = () => {
    navigate(`/matters/${matterId}/treatment/${providerId}/edit`);
  };

  const handleStatusUpdate = async (action: 'approve' | 'reject') => {
    if (isUpdating) return;

    setIsUpdating(true);
    try {
      if (action === 'approve') {
        await approveProvider({ matterId, providerId }).unwrap();
        toast.success('Provider marked as reviewed');
      } else {
        await rejectProvider({ matterId, providerId }).unwrap();
        toast.success('Provider rejected');
      }
      refetch();
    } catch (error) {
      toast.error(
        `Failed to ${action === 'approve' ? 'review' : 'reject'} provider`,
      );
    } finally {
      setIsUpdating(false);
    }
  };

  const handleTreatmentStatusUpdate = async (
    action: 'still_treating' | 'done_treating',
  ) => {
    if (isUpdating) return;

    setIsUpdating(true);
    try {
      if (action === 'still_treating') {
        await markStillTreating({ matterId, providerId }).unwrap();
        toast.success('Marked as still treating');
      } else {
        await markDoneTreating({ matterId, providerId }).unwrap();
        toast.success('Marked as done treating');
      }
      refetch();
    } catch (error) {
      toast.error('Failed to update treatment status');
    } finally {
      setIsUpdating(false);
    }
  };

  if (isLoading) {
    return (
      <div className="h-full bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-xl font-semibold text-gray-900 mb-2">
            Loading Provider Details...
          </h1>
          <p className="text-gray-600">
            Please wait while we fetch the provider information.
          </p>
        </div>
      </div>
    );
  }

  if (loadError || !provider) {
    return (
      <div className="h-full bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-xl font-semibold text-gray-900 mb-2">
            Error Loading Provider
          </h1>
          <p className="text-gray-600 mb-4">
            Unable to load provider information.
          </p>
          <Button onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            {backButtonText}
          </Button>
        </div>
      </div>
    );
  }

  // Determine status states
  const needsApproval =
    !provider.status || provider.status === ProviderStatus.NEEDS_APPROVAL;
  const isApproved = provider.status === ProviderStatus.APPROVED;
  const isRejected = provider.status === ProviderStatus.REJECTED;

  // Check if we have evidence to show
  const hasEvidence = provider.mentions && provider.mentions.length > 0;

  const currentStatus = getSimpleTreatmentStatus(provider.treatment_status);
  const handleManageDestinationsInEdit = () => {
    navigate(`/matters/${matterId}/treatment/${providerId}/edit?tab=contact`);
  };
  const requestDestinationsCard = (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-gray-900">
          Request Destinations
        </h2>
        <Button
          variant="outline"
          size="sm"
          onClick={handleManageDestinationsInEdit}
        >
          Manage in Edit
        </Button>
      </div>

      {!provider.provider_service_ref_id && (
        <p className="text-sm text-gray-500">
          Link this provider to the directory to configure request destinations.
        </p>
      )}

      {provider.provider_service_ref_id && (
        <div className="space-y-2">
          {DESTINATION_SCOPES.map((scope) => {
            const block = getScopeDestinations(scope);
            const recommended = block?.recommended;
            let destinationValue = 'Not set';
            if (recommended) {
              destinationValue = `${recommended.channel_type}: ${recommended.value}`;
            } else if (isDestinationsLoading) {
              destinationValue = 'Loading...';
            }
            return (
              <div
                key={scope}
                className="rounded-md border border-gray-100 bg-gray-50 px-3 py-2"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-semibold text-gray-600">
                    {DESTINATION_SCOPE_LABELS[scope]}
                  </span>
                  {block?.research_status === 'IN_PROGRESS' && (
                    <span className="text-xs text-blue-600">Researching...</span>
                  )}
                </div>
                <p className="mt-1 truncate text-sm text-gray-800">
                  {destinationValue}
                </p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <div className="h-full overflow-y-auto bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between gap-4">
            {/* Left side - Navigation and Provider Info */}
            <div className="flex items-center gap-4 min-w-0 flex-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={onBack}
                className="gap-2 flex-shrink-0"
              >
                <ArrowLeft className="h-4 w-4" />
                {backButtonText}
              </Button>
              <Separator orientation="vertical" className="h-6 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <h1 className="text-xl font-medium text-gray-900 truncate">
                  {provider.name || 'Unknown Provider'}
                </h1>
                <div className="flex items-center gap-3 mt-1">
                  {provider.provider_type && (
                    <span className="text-sm text-gray-600 truncate">
                      {
                        {
                          [ProviderType.ORGANIZATION]: 'Organization',
                          [ProviderType.PRACTITIONER]: 'Practitioner',
                          [ProviderType.HOSPITAL]: 'Hospital',
                          [ProviderType.EMS]: 'EMS',
                          [ProviderType.URGENT_CARE]: 'Urgent Care',
                        }[provider.provider_type] ?? provider.provider_type
                      }
                    </span>
                  )}
                  {provider.address && (
                    <span className="text-sm text-gray-500 truncate">
                      {provider.address}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Right side - Status and Actions */}
            <div className="flex items-center gap-3 flex-shrink-0">
              <ProviderStatusBadge status={provider.status} />

              {/* Open in Directory Button */}
              <OpenInDirectoryButton
                providerServiceRefId={provider.provider_service_ref_id}
                variant="text"
              />

              {/* Edit Provider Button */}
              <Button
                size="sm"
                variant="outline"
                onClick={handleEdit}
                className="gap-2"
              >
                <Edit2 className="h-4 w-4" />
                <span className="hidden lg:inline">Edit</span>
              </Button>

              {/* Treatment Status Badge */}
              {currentStatus === SimpleTreatmentStatus.COMPLETED && (
                <span className="inline-flex items-center rounded-md px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800">
                  No Longer Treating
                </span>
              )}

              {/* Treatment Status Actions */}
              {isApproved && (
                <div className="hidden sm:flex items-center gap-2">
                  {/* Show both buttons when status is unknown (initial state) */}
                  {currentStatus === SimpleTreatmentStatus.UNKNOWN && (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          handleTreatmentStatusUpdate('still_treating')
                        }
                        disabled={isUpdating}
                        className="gap-2 text-green-600 hover:text-green-700 hover:bg-green-50 border-green-200"
                      >
                        <UserCheck className="h-4 w-4" />
                        <span className="hidden lg:inline">Still Treating</span>
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          handleTreatmentStatusUpdate('done_treating')
                        }
                        disabled={isUpdating}
                        className="gap-2 text-gray-600 hover:text-gray-700 hover:bg-gray-50"
                      >
                        <UserX className="h-4 w-4" />
                        <span className="hidden lg:inline">Done Treating</span>
                      </Button>
                    </>
                  )}

                  {/* Show primary "Done Treating" button when currently still treating */}
                  {currentStatus === SimpleTreatmentStatus.TREATING && (
                    <Button
                      size="sm"
                      onClick={() =>
                        handleTreatmentStatusUpdate('done_treating')
                      }
                      disabled={isUpdating}
                      className="gap-2"
                    >
                      <UserX className="h-4 w-4" />
                      <span className="hidden lg:inline">
                        Mark Done Treating
                      </span>
                    </Button>
                  )}
                </div>
              )}

              {/* Review Actions */}
              {needsApproval && (
                <div className="hidden sm:flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleStatusUpdate('reject')}
                    disabled={isUpdating}
                    className="gap-2 text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                  >
                    <X className="h-4 w-4" />
                    <span className="hidden lg:inline">Reject</span>
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => handleStatusUpdate('approve')}
                    disabled={isUpdating}
                    className="gap-2"
                  >
                    <Check className="h-4 w-4" />
                    <span className="hidden lg:inline">Mark Reviewed</span>
                  </Button>
                </div>
              )}

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {/* Mobile actions */}
                  {isApproved && (
                    <>
                      {/* Mobile - show appropriate buttons based on current state */}
                      {currentStatus === SimpleTreatmentStatus.UNKNOWN && (
                        <>
                          <DropdownMenuItem
                            onClick={() =>
                              handleTreatmentStatusUpdate('still_treating')
                            }
                            className="sm:hidden"
                          >
                            <UserCheck className="h-4 w-4 mr-2" />
                            Still Treating
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() =>
                              handleTreatmentStatusUpdate('done_treating')
                            }
                            className="sm:hidden"
                          >
                            <UserX className="h-4 w-4 mr-2" />
                            Done Treating
                          </DropdownMenuItem>
                        </>
                      )}

                      {currentStatus === SimpleTreatmentStatus.TREATING && (
                        <DropdownMenuItem
                          onClick={() =>
                            handleTreatmentStatusUpdate('done_treating')
                          }
                          className="sm:hidden"
                        >
                          <UserX className="h-4 w-4 mr-2" />
                          Mark Done Treating
                        </DropdownMenuItem>
                      )}

                      {/* Backwards action - always in dropdown */}
                      {currentStatus === SimpleTreatmentStatus.COMPLETED && (
                        <DropdownMenuItem
                          onClick={() =>
                            handleTreatmentStatusUpdate('still_treating')
                          }
                        >
                          <UserCheck className="h-4 w-4 mr-2" />
                          Mark Still Treating
                        </DropdownMenuItem>
                      )}
                    </>
                  )}

                  {needsApproval && (
                    <>
                      <DropdownMenuItem
                        onClick={() => handleStatusUpdate('approve')}
                        className="sm:hidden"
                      >
                        <Check className="h-4 w-4 mr-2" />
                        Mark Reviewed
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleStatusUpdate('reject')}
                        className="sm:hidden text-red-600 focus:text-red-600"
                      >
                        <X className="h-4 w-4 mr-2" />
                        Reject
                      </DropdownMenuItem>
                    </>
                  )}

                  {(isApproved || isRejected) && (
                    <DropdownMenuItem
                      onClick={() => {
                        setIsUpdating(true);
                        updateProvider({
                          matterId,
                          providerId,
                          provider: { status: ProviderStatus.NEEDS_APPROVAL },
                        })
                          .unwrap()
                          .then(() => {
                            refetch();
                            toast.success('Reset to pending approval');
                          })
                          .catch(() => {
                            toast.error('Failed to reset status');
                          })
                          .finally(() => {
                            setIsUpdating(false);
                          });
                      }}
                    >
                      <Clock className="h-4 w-4 mr-2" />
                      Reset to Pending
                    </DropdownMenuItem>
                  )}

                  <DropdownMenuItem
                    onClick={() => setIsDeleteDialogOpen(true)}
                    className="text-red-600 focus:text-red-600"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Provider
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <div className="max-w-7xl mx-auto">
          {hasEvidence ? (
            /* Two-column layout with evidence */
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
              {/* Left Column - Provider Details */}
              <div className="lg:col-span-2 space-y-4">
                <ProviderInfoDisplay
                  provider={provider}
                  topContent={requestDestinationsCard}
                />
              </div>

              {/* Right Column - Treatment, Requests, Evidence */}
              <div className="lg:col-span-3 space-y-6">
                {/* Encounters */}
                <div>
                  <EncountersOverview
                    matterId={matterId}
                    providerId={providerId}
                  />
                </div>

                <RecentRequestsList
                  matterId={matterId}
                  providerId={providerId}
                  onNewRequest={() => setIsCreateRequestOpen(true)}
                />

                {/* Evidence */}
                <div>
                  <h2 className="text-lg font-medium text-gray-900 mb-4">
                    Sources
                  </h2>
                  <div className="space-y-6">
                    {provider.mentions?.map((mention, index) => (
                      <div key={mention.id} className="space-y-3">
                        <div className="flex items-center gap-2">
                          <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs font-medium">
                            Source #{index + 1}
                          </span>
                        </div>
                        <EvidenceCard
                          evidence={{
                            artifact_id: mention.artifact_id,
                            citation_ids: mention.citation_ids,
                            context: mention.excerpts.join(' '),
                            created_at: new Date().toISOString(), // Use current date as fallback
                            artifact_type: mention.artifact_type,
                          }}
                          matterId={matterId}
                          showTimestamp
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* Single column layout without evidence */
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column */}
              <div className="space-y-4">
                <ProviderInfoDisplay
                  provider={provider}
                  topContent={requestDestinationsCard}
                />
              </div>

              {/* Right Column */}
              <div className="space-y-6">
                {/* Encounters */}
                <div>
                  <EncountersOverview
                    matterId={matterId}
                    providerId={providerId}
                  />
                </div>

                <RecentRequestsList
                  matterId={matterId}
                  providerId={providerId}
                  onNewRequest={() => setIsCreateRequestOpen(true)}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <CreateProviderRequestDialog
        isOpen={isCreateRequestOpen}
        setIsOpen={setIsCreateRequestOpen}
        provider={provider}
      />

      <Dialog open={isDeleteDialogOpen} onClose={setIsDeleteDialogOpen}>
        <DialogTitle>Delete provider</DialogTitle>
        <DialogDescription>
          Are you sure you want to delete this provider? This action cannot be
          undone.
        </DialogDescription>
        <DialogActions>
          <CatalystButton plain onClick={() => setIsDeleteDialogOpen(false)}>
            Cancel
          </CatalystButton>
          <CatalystButton
            color="red"
            onClick={handleDelete}
            className="flex flex-row items-center gap-2"
          >
            {isDeleting && <Loader className="h-4 w-4 animate-spin" />}
            Delete
          </CatalystButton>
        </DialogActions>
      </Dialog>
    </div>
  );
};
